<template>
    <div class="p-4 bg-white sm:p-8 sm:border-b-0 border-b-1 sm:border-transparent border-clay-300">
        <div class="mx-auto max-100">
            <div class="md:flex md:items-center md:justify-between">
                <div class="flex-1 min-w-0">
                    <a href="/"><img alt="" class="w-auto h-8 mx-auto sm:mx-2 sm:blsock img-fluid" src="/public/assets/img/logo.png"></a>
                </div>
                <div class="mt-2 text-center sm:flex md:ml-4 md:mt-0">
                    <h2 class="italic leading-7 text-clay-900 text-md sm:truncate sm:text-xl sm:tracking-tight">The leading resource for theatre artists</h2>
                </div>
            </div>
        </div>
    </div>
    <div class="hidden lg:border-t lg:border-b lg:border-clay-200 sm:block">
        <nav aria-label="Progress">
            <ol class="border divide-y rounded-md divide-clay-300 border-clay-500 md:flex md:divide-y-0" role="list">

                <li v-for="s in steps" class="relative md:flex md:flex-1">
                    <!-- Past Steps -->
                    <div v-if="s.step < currentStep" :title="s.name" aria-current="step"
                        class="flex items-center px-6 py-4 text-sm font-medium cursor-pointer"
                        @click="currentStep = s.step">
                        <div
                            class="flex items-center justify-center w-10 h-10 rounded-full shrink-0 bg-electric-600 group-hover:bg-electric-800">
                            <svg aria-hidden="true" class="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24"
                                xmlns="http://www.w3.org/2000/svg">
                                <path clip-rule="evenodd"
                                    d="M19.916 4.626a.75.75 0 01.208 1.04l-9 13.5a.75.75 0 01-1.154.114l-6-6a.75.75 0 011.06-1.06l5.353 5.353 8.493-12.739a.75.75 0 011.04-.208z"
                                    fill-rule="evenodd" />
                            </svg>
                        </div>
                        <span class="ml-4 text-sm font-medium text-clay-900">{{ s.name }}</span>
                    </div>
                    <!-- Current Step -->
                    <div v-if="s.step === currentStep" :title="s.name" aria-current="step"
                        class="flex items-center px-6 py-4 text-sm font-medium">
                        <div
                            class="flex items-center justify-center w-10 h-10 border-2 rounded-full shrink-0 bg-electric-600 border-electric-600">
                            <span class="text-white">{{ s.step }}</span>
                        </div>
                        <span class="ml-4 text-sm font-medium text-electric-600">{{ s.name }}</span>
                    </div>
                    <!-- Future Steps -->
                    <div v-if="s.step > currentStep" :title="s.name" aria-current="step"
                        class="flex items-center px-6 py-4 text-sm font-medium">
                        <div class="flex items-center justify-center w-10 h-10 border-2 rounded-full shrink-0 border-clay-500">
                            <span class="text-clay-500 group-hover:text-clay-900">{{ s.step }}</span>
                        </div>
                        <span class="ml-4 text-sm font-medium text-clay-500 group-hover:text-clay-900">{{
                            s.name
                            }}</span>
                    </div>

                    <!-- Arrow separator for lg screens and up -->
                    <div aria-hidden="true" class="absolute top-0 right-0 hidden w-5 h-full md:block">
                        <svg class="w-full h-full text-clay-300" fill="none" preserveAspectRatio="none"
                            viewBox="0 0 22 80">
                            <path d="M0 -2L20 40L0 82" stroke="currentcolor" stroke-linejoin="round"
                                vector-effect="non-scaling-stroke" />
                        </svg>
                    </div>
                </li>

            </ol>
        </nav>
    </div>
    <div class="min-h-screen sm:pt-12 bg-clay-50">
        
        <div class="flex flex-col justify-center min-h-full px-4 py-0 py-4 sm:py-12 sm:px-6 lg:px-8">
            <div class="sm:mx-auto sm:w-full sm:max-w-5xl">

                <OnboardingStepBasicProfileInfo v-if="currentStep === 1" key="1" @on-next="next" />

                <OnboardingStepRegion v-if="currentStep === 2" key="2" @on-back="back" @on-next="next" />

                <OnboardingStepGoals v-if="currentStep === 3" key="3" @on-back="back" @on-next="next" />

                <OnboardingStepHeadshot v-if="currentStep === 4" key="4" @on-back="back" @on-next="next" />
                
                <OnboardingStepUpgradeToPro v-if="currentStep === 5" key="5" @on-back="back" @on-next="next" />

                <OnboardingStepNextSteps v-if="currentStep === 6" key="6" @on-back="back" />

            </div>
        </div>

    </div>
</template>

<script lang="ts" setup>
// @ts-ignore
import {Ref, ref} from "vue";
// @ts-ignore
import {ResponseStatus} from "@servicestack/client";
import OnboardingStepHeadshot from "@/../Features/Onboarding/OnboardingStepHeadshot.vue";
import OnboardingStepRegion from "@/../Features/Onboarding/OnboardingStepRegion.vue";
import OnboardingStepManagingAccountFor from "./OnboardingStepManagingAccountFor.vue";
import OnboardingStepGoals from "./OnboardingStepGoals.vue";
import OnboardingStepUpgradeToPro from "./OnboardingStepUpgradeToPro.vue";
import OnboardingStepNextSteps from "./OnboardingStepNextSteps.vue";
import OnboardingStepBasicProfileInfo from "./OnboardingStepBasicProfileInfo.vue";

const steps = ref([
    {step: 1, name: "Welcome"},
    {step: 2, name: "Region"},
    {step: 3, name: "Goals"}, 
    {step: 4, name: "Headshot"},
    {step: 5, name: "Upgrade to PRO"},
    {step: 6, name: "Next steps"}
]);

const currentStep = ref(1);

const back = async () => {
    currentStep.value--;
};

const next = async () => {
    currentStep.value++;

    if (currentStep.value > steps.value.length) {
        window.location.href = "/membership/";
    } else {
        window.scrollTo({
            top: 0,
            behavior: 'smooth'
        });
    }
};

</script>
<style>

</style>